import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MAX_COLUMNS = 6;

/**
 * Edits a reference table — the grids in his supplements (oils, ingredients, endocrine
 * disruptors). Read-only for members, so there is nothing here about answers.
 */
export const TableFieldEditor = ({ field, onChange, index }) => {
  const fillable = field.type === 'table_fill';
  const columns = field.table_columns || [];
  const rows = field.table_rows || [];
  const types = field.table_column_types || [];
  const columnOptions = field.table_column_options || [];

  const setType = (c, value) => {
    const next = [...types];
    while (next.length < columns.length) next.push('text');
    next[c] = value;
    onChange({ table_column_types: next });
  };

  const setOptions = (c, value) => {
    const next = columnOptions.map((o) => [...(o || [])]);
    while (next.length < columns.length) next.push([]);
    next[c] = value.split(',').map((o) => o.trim()).filter(Boolean);
    onChange({ table_column_options: next });
  };

  const setColumn = (c, value) => {
    const next = [...columns];
    next[c] = value;
    onChange({ table_columns: next });
  };

  const addColumn = () => onChange({
    table_columns: [...columns, ''],
    table_rows: rows.map((r) => [...r, '']),
    table_column_types: [...types, 'text'],
    table_column_options: [...columnOptions, []],
  });

  const removeColumn = (c) => onChange({
    table_columns: columns.filter((_, i) => i !== c),
    table_rows: rows.map((r) => r.filter((_, i) => i !== c)),
    table_column_types: types.filter((_, i) => i !== c),
    table_column_options: columnOptions.filter((_, i) => i !== c),
  });

  const setCell = (r, c, value) => {
    const next = rows.map((row) => [...row]);
    while (next[r].length < columns.length) next[r].push('');
    next[r][c] = value;
    onChange({ table_rows: next });
  };

  const addRow = () => onChange({ table_rows: [...rows, columns.map(() => '')] });
  const removeRow = (r) => onChange({ table_rows: rows.filter((_, i) => i !== r) });

  const cell = 'w-full rounded bg-black/30 border border-white/10 px-2 py-1 text-xs text-gray-100';

  return (
    <div className="space-y-2" data-testid={`worksheet-table-editor-${index}`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: columns.length * 130 }}>
          <thead>
            <tr>
              {columns.map((header, c) => (
                <th key={c} className="p-1 align-top">
                  <div className="flex items-center gap-1">
                    <input value={header} onChange={(e) => setColumn(c, e.target.value)}
                      placeholder={`Column ${c + 1}`}
                      className={`${cell} font-semibold`}
                      data-testid={`worksheet-table-col-${index}-${c}`} />
                    {columns.length > 1 && (
                      <button type="button" onClick={() => removeColumn(c)}
                        className="text-gray-500 hover:text-red-400"
                        title="Remove this column"
                        data-testid={`worksheet-table-remove-col-${index}-${c}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {fillable && (
                    <div className="mt-1 space-y-1">
                      <select value={types[c] || 'text'} onChange={(e) => setType(c, e.target.value)}
                        className={`${cell} font-normal`}
                        data-testid={`worksheet-table-type-${index}-${c}`}>
                        <option value="text">Text</option>
                        <option value="number">Number</option>
                        <option value="choice">Pick from a list</option>
                      </select>
                      {(types[c] || 'text') === 'choice' && (
                        <input value={(columnOptions[c] || []).join(', ')}
                          onChange={(e) => setOptions(c, e.target.value)}
                          placeholder="daily, most days, weekly, drifted"
                          className={`${cell} font-normal`}
                          data-testid={`worksheet-table-options-${index}-${c}`} />
                      )}
                    </div>
                  )}
                </th>
              ))}
              <th className="w-6" />
            </tr>
          </thead>
          <tbody>
            {fillable ? null : rows.map((row, r) => (
              <tr key={r}>
                {columns.map((_, c) => (
                  <td key={c} className="p-1 align-top">
                    <textarea rows={2} value={row[c] || ''}
                      onChange={(e) => setCell(r, c, e.target.value)}
                      className={`${cell} leading-snug resize-y`}
                      data-testid={`worksheet-table-cell-${index}-${r}-${c}`} />
                  </td>
                ))}
                <td className="p-1 align-top">
                  <button type="button" onClick={() => removeRow(r)}
                    className="text-gray-500 hover:text-red-400" title="Remove this row"
                    data-testid={`worksheet-table-remove-row-${index}-${r}`}>
                    <X className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!fillable && (
          <Button size="sm" variant="outline" onClick={addRow}
            className="border-white/20 text-gray-100 hover:bg-white/10 h-7 text-xs"
            data-testid={`worksheet-table-add-row-${index}`}>
            <Plus className="w-3 h-3 mr-1" /> Row
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={addColumn}
          disabled={columns.length >= MAX_COLUMNS}
          className="border-white/20 text-gray-100 hover:bg-white/10 h-7 text-xs"
          data-testid={`worksheet-table-add-col-${index}`}>
          <Plus className="w-3 h-3 mr-1" /> Column
        </Button>

        {fillable ? (
          <>
            <input type="number" min={1} max={15} value={field.table_start_rows || 3}
              onChange={(e) => onChange({
                table_start_rows: parseInt(e.target.value, 10) || 3,
              })}
              className="w-16 rounded bg-black/30 border border-white/10 px-2 py-1 text-xs
                         text-gray-100" 
              data-testid={`worksheet-table-start-rows-${index}`} />
            <span className="text-[11px] text-gray-500">
              blank rows to start with — they can add more · {columns.length} of {MAX_COLUMNS}
              {' '}columns
            </span>
          </>
        ) : (
          <span className="text-[11px] text-gray-500">
            {columns.length} of {MAX_COLUMNS} columns · {rows.length}
            {' '}row{rows.length === 1 ? '' : 's'} · reading only, nothing for them to fill in
          </span>
        )}
      </div>
    </div>
  );
};

export default TableFieldEditor;
